import React, { useState } from "react";
import { Formik, Form, Field } from "formik";
import { Box, IconButton, InputAdornment } from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import { NavLink, useNavigate } from "react-router-dom";
import API_BASE_URL from "../../components/config/appConfig";
import { toLowerCaseEmail } from "../../utilities/email";
import { updatePasswordSchema, EmailVerificationSchema } from "../../Validations/schema";
import CryptoJS from 'crypto-js';
import OTPInput, { ResendOTP } from "otp-input-react";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { AuthSecureArt } from "./authArt";
import { IconShield, IconBell, IconCheck } from "./landingArt";
import {
  AuthLayout,
  AuthNotice,
  AuthTextField,
  AuthPrimaryButton,
  AuthError,
  AuthRise,
  BRAND_DEEP,
  EASE
} from "./authUi";
import "./home.scss";

const STAGE_POINTS = [
  { icon: IconBell, label: "Code by email" },
  { icon: IconShield, label: "Secure reset" },
  { icon: IconCheck, label: "Back in seconds" },
];

const ForgotPassword = () => {

  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState({ emailId: '', email: false, otp: false, invalidOtp: false });
  const [OTP, setOTP] = useState("");
  const [createdOtp, setCreateOtp] = useState("");
  const [resendVerify, setResendVerify] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  let timeoutId;

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  const getSendOtpToMail = async (email) => {
    const creatOtp = Math.floor(Math.random() * 899999 + 100000);
    const secretKey = 'Pr0JaM0Tp';
    // Use a strong, unique key
    const encryptedOtp = await CryptoJS.AES.encrypt(JSON.stringify(creatOtp), secretKey).toString();

    const data = {
      email: toLowerCaseEmail(email),
      otp: encryptedOtp,
    };
    try {
      const response = await fetch(`${API_BASE_URL}/users/verify-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
      if (response.status === 200) {
        setCreateOtp(creatOtp);
        setIsEmailVerified((prevState) => ({ ...prevState, email: true, emailId: email, otp: false, invalidOtp: false }))
        setResendVerify(false);
        getResendButtonActive()
      }
    } catch (err) {
      setError("An error occurred. Please try again later.");
    }
  }

  const getResendButtonActive = () => {
    if (!isEmailVerified?.invalidOtp) {
      timeoutId = setTimeout(() => {
        setResendVerify(true)
      }, 60000);
    }
  }

  const handleSubmit = async (values) => {
    const loginData = {
      email: toLowerCaseEmail(values.email)
    };
    try {
      const response = await fetch(`${API_BASE_URL}/users/verify-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(loginData),
      });
      if (response.ok) {
        await getSendOtpToMail(values.email);
      } else {
        let responsess = await response.json()
        const errorMessage = await responsess.message;
        setError(errorMessage);
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.log(error, 'catch')
      setError("An error occurred. Please try again later.");
      setSnackbarOpen(true);
    }
  };

  const handleUpdatePassword = async (values) => {
    const loginData = {
      ...values,
      email: toLowerCaseEmail(values.email)
    };
    if (values.password === values.confirmPassword) {
      try {
        const response = await fetch(`${API_BASE_URL}/users/update-password`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(loginData),
        });
        if (response?.ok) {
          navigate("/login");
        } else {
          let responsess = await response.json()
          const errorMessage = await responsess.message;
          setError(errorMessage);
          setSnackbarOpen(true);
        }
      } catch (error) {
        console.log(error, 'catch')
        setError("An error occurred. Please try again later.");
        setSnackbarOpen(true);
      }
    } else {
      setError("Passwords not matched!");
      setSnackbarOpen(true);
    }
  };

  const onChangeVerifyOtp = () => {
    if (OTP == createdOtp) {
      setIsEmailVerified((prevState) => ({ ...prevState, otp: true, invalidOtp: false }))
      setResendVerify(false);
    } else {
      setError("Invalid Otp, Please Try Again!");
      setSnackbarOpen(true);
      setIsEmailVerified((prevState) => ({ ...prevState, otp: false, invalidOtp: true }));
      setResendVerify(true);
      setCreateOtp("")
      setOTP('')

    }
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
  };

  const renderButton = (buttonProps) => {
    return <div className="au-otp-actions">
      {!isEmailVerified?.invalidOtp ? <><button
        disabled={OTP.length !== 6}
        className="btn btn-success"
        onClick={onChangeVerifyOtp}
      >
        Verify Otp
      </button>

        <p className="fw-bold">Or</p></> : null}
      <button
        {...buttonProps}
        className="btn btn-secondary"
        disabled={!resendVerify}
      >
        Resend Otp
      </button>
    </div>

  };
  const renderTime = (remainingTime) => {
    return (!resendVerify && remainingTime !== 0) ? <span>{remainingTime} seconds remaining</span> : "";

  };

  const notice = (
    <AuthNotice open={snackbarOpen} onClose={handleSnackbarClose}>
      {error}
    </AuthNotice>
  );

  const awaitingOtp = isEmailVerified?.email && !isEmailVerified?.otp;
  const heading = !isEmailVerified?.email
    ? "Reset your password"
    : awaitingOtp
      ? "Check your email"
      : "Choose a new password";
  const subheading = !isEmailVerified?.email
    ? "Enter your email to continue"
    : awaitingOtp
      ? "We sent a 6-digit code to your inbox"
      : "Pick something you have not used before";

  return (
    <AuthLayout
      notice={notice}
      title={<>Locked out?<br /><em>Back in a minute.</em></>}
      lead="We email you a one-time code, you confirm it is you, and you pick a new password."
      points={STAGE_POINTS}
      art={<AuthSecureArt />}
    >
      <h2 className="au-title au-rise" style={{ '--au-d': '.06s' }}>
        {heading}
      </h2>
      <p className="au-sub au-rise" style={{ '--au-d': '.12s' }}>
        {subheading}
      </p>

      {(!isEmailVerified?.email || !isEmailVerified?.otp) ?
        !isEmailVerified?.email ? <Formik
          initialValues={{
            email: ""
          }}
          validationSchema={EmailVerificationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form>
              <AuthRise delay={0.18} mb={3.5}>
                <Field name="email">
                  {({ field }) => (
                    <AuthTextField
                      {...field}
                      label="Email Address"
                      variant="outlined"
                      fullWidth
                      placeholder="Enter your email address"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                </Field>
                <AuthError name="email" />
              </AuthRise>

              <AuthRise delay={0.24}>
                <AuthPrimaryButton
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={isSubmitting}
                  size="large"
                  disableElevation
                >
                  {isSubmitting ? "Verifying email..." : "Continue"}
                </AuthPrimaryButton>
              </AuthRise>
            </Form>
          )}
        </Formik> : (
          <AuthRise delay={0.18} className="au-otp">
            <OTPInput
              value={OTP}
              onChange={setOTP}
              autoFocus
              OTPLength={6}
              otpType="number"
              disabled={createdOtp?.toString()?.length === 6 ? false : true}
              secure
              inputClassName="input-class-name"
            />
            <p className="au-otp-hint">
              Sent to <strong>{isEmailVerified?.emailId}</strong>
            </p>
            <ResendOTP
              onResendClick={() => getSendOtpToMail(isEmailVerified?.emailId)}
              className="au-resend"
              renderButton={renderButton}
              renderTime={renderTime}
            />
          </AuthRise>
        ) : <Formik
          initialValues={{
            email: isEmailVerified?.emailId,
            password: '',
            confirmPassword: ''
          }}
          validationSchema={updatePasswordSchema}
          onSubmit={handleUpdatePassword}
        >
          {({ isSubmitting }) => (
            <Form>
              <AuthRise delay={0.18} mb={2.5}>
                <Field name="email">
                  {({ field }) => (
                    <AuthTextField
                      {...field}
                      label="Email Address"
                      variant="outlined"
                      disabled={isEmailVerified?.emailId}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                </Field>
                <AuthError name="email" />
              </AuthRise>

              <AuthRise delay={0.24} mb={2.5}>
                <Field name="password">
                  {({ field }) => (
                    <AuthTextField
                      {...field}
                      label="Password"
                      variant="outlined"
                      fullWidth
                      placeholder="Enter your new password"
                      type={showPassword ? "text" : "password"}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleTogglePasswordVisibility}
                              edge="end"
                              sx={{
                                color: '#98a0b0',
                                transition: `color .25s ${EASE}`,
                                '&:hover': { color: BRAND_DEEP, background: 'rgba(0, 172, 86, 0.08)' },
                              }}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                </Field>
                <AuthError name="password" />
              </AuthRise>

              <AuthRise delay={0.3} mb={3.5}>
                <Field name="confirmPassword">
                  {({ field }) => (
                    <AuthTextField
                      {...field}
                      label="Confirm Password"
                      variant="outlined"
                      fullWidth
                      placeholder="Re-enter your new password"
                      type={showConfirmPassword ? "text" : "password"}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleToggleConfirmPasswordVisibility}
                              edge="end"
                              sx={{
                                color: '#98a0b0',
                                transition: `color .25s ${EASE}`,
                                '&:hover': { color: BRAND_DEEP, background: 'rgba(0, 172, 86, 0.08)' },
                              }}
                            >
                              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                </Field>
                <AuthError name="confirmPassword" />
              </AuthRise>

              <AuthRise delay={0.36}>
                <AuthPrimaryButton
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={isSubmitting}
                  size="large"
                  disableElevation
                >
                  {isSubmitting ? "Updating..." : "Continue"}
                </AuthPrimaryButton>
              </AuthRise>
            </Form>
          )}
        </Formik>}

      <Box className="au-foot au-rise" style={{ '--au-d': '.42s' }}>
        Remembered it?
        <NavLink className="au-link" to="/login">Back to sign in</NavLink>
      </Box>
    </AuthLayout>
  );
};

export default ForgotPassword;
